import * as React from 'react';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

function ConfirmationModal({
    isOpen,
    onOpenChange,
    title = 'Confirm Action',
    description = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    variant = 'default',
    zIndex = 'z-[5000]',
    hideCancel = false,
    center = false,
    icon: Icon = null,
    extraActions = []
}) {
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        } else if (onOpenChange) {
            onOpenChange(false);
        }
    };

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
        // Success modal in AuthPromptDialog handles closing via hard refresh
        // but for others we still close normally
        if (onOpenChange && title !== "Subscription Activated!") {
            onOpenChange(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className={cn(
            "p-0 gap-0 [&>button]:text-[hsl(var(--foreground))] [&>button]:opacity-100 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground shadow-2xl",
            "md:max-w-md",
            zIndex
        )}>
                <div className="relative p-6 pb-4">
                    <div className="absolute -top-12 -right-12 w-32 h-32 bg-[hsl(var(--primary))]/20 rounded-full blur-2xl pointer-events-none" />
                    
                    <DialogHeader className={cn("relative z-10", center && "text-center sm:text-center")}>
                        <div className={cn("flex items-center gap-3 mb-2", center && "justify-center")}>
                            {Icon && (
                                <div className={cn(
                                    "p-2.5 rounded-xl shadow-lg transition-all duration-300",
                                    variant === 'destructive' 
                                        ? "bg-gradient-to-br from-red-500 to-rose-600 shadow-red-500/20" 
                                        : "bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] shadow-emerald-500/20"
                                )}>
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                            )}
                            <DialogTitle className="text-xl font-bold tracking-tight text-[hsl(var(--foreground))]">{title}</DialogTitle>
                        </div>
                        <DialogDescription className="text-muted-foreground leading-relaxed">{description}</DialogDescription>
                    </DialogHeader>
                </div>

                <DialogFooter className={cn(
                    "px-6 pb-6 pt-2 flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-3 sm:gap-3",
                    (hideCancel || center) && "sm:justify-center"
                )}>
                    {!hideCancel && (
                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            className="w-full sm:flex-1 sm:min-w-[120px] border-[hsl(var(--card-border))] bg-transparent text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-all rounded-lg min-h-10 h-auto px-4 py-2 font-semibold text-center whitespace-normal leading-snug"
                        >
                            {cancelText}
                        </Button>
                    )}
                    {extraActions.map((action, index) => (
                        <Button
                            key={`${action?.label || 'extra'}-${index}`}
                            variant={action?.variant || 'outline'}
                            onClick={() => {
                                action?.onClick?.();
                                if (action?.closeOnClick !== false && onOpenChange) {
                                    onOpenChange(false);
                                }
                            }}
                            className={cn(
                                "w-full sm:flex-1 sm:min-w-[120px] min-h-10 h-auto px-4 py-2 rounded-lg font-semibold transition-all text-center whitespace-normal leading-snug",
                                action?.className || "border-[hsl(var(--card-border))] bg-transparent text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                            )}
                        >
                            {action?.label}
                        </Button>
                    ))}
                    <Button
                        onClick={handleConfirm}
                        className={cn(
                            "w-full sm:flex-1 sm:min-w-[160px] font-semibold transition-all min-h-10 h-auto rounded-lg px-4 py-2 shadow-sm text-center whitespace-normal leading-snug",
                            variant === 'destructive'
                                ? "bg-red-500 hover:bg-red-600 text-white"
                                : "bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-95 text-white shadow-emerald-500/20",
                            hideCancel && "sm:max-w-[220px]"
                        )}
                    >
                        {confirmText}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { ConfirmationModal };
